import React, { useEffect, useMemo, useState } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Editor, Toolbar } from '@wangeditor/editor-for-react';
import {
  Button,
  Checkbox,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useSnapshot } from 'valtio';
import {
  deleteBenefitApi,
  getBenefitsListApi,
  saveOrUpdateBenefitApi,
  switchBenefitApi,
  viewBenefitApi,
} from '@/api/member/member';
import ImageUploader from '@/component/ImageUploader';
import { state } from '@/store';
import '@wangeditor/editor/dist/css/style.css';
import dayjs from 'dayjs';
import { BENEFITS_KEY } from '@/router/Permissions';
import { defaultEditorConfig, defaultToolbarConfig } from '@/utils/editorConfig';
import { checkPermissions } from '@/utils/utils';
import BenefitSection from '../components/BenefitSection';
import styles from './index.module.scss';

const { Title } = Typography;

type BenefitSectionConfig = {
  title: string;
  benefitsType: string;
  fetchErrorText?: string;
  showDelete?: boolean;
  showAddCard?: boolean;
  addButtonText?: string;
  allowView?: boolean;
  allowEdit?: boolean;
  allowSwitch?: boolean;
  allowDelete?: boolean;
};

type BenefitsManagementProps = {
  pageTitle?: string;
  modalTitlePrefix?: string;
  sections?: BenefitSectionConfig[];
  switchPermissionKey?: string;
};

const DEFAULT_SECTIONS: BenefitSectionConfig[] = [
  { title: '基础权益', benefitsType: 'BASIC', fetchErrorText: '获取基础权益失败' },
  {
    title: '其他权益',
    benefitsType: 'OTHER',
    fetchErrorText: '获取其他权益失败',
    showDelete: true,
    showAddCard: true,
    addButtonText: '添加其他权益',
  },
];

const isCustomBenefitType = (benefitsType?: string) => benefitsType === 'OTHER';
const hasShopGrowthExtendFields = (detail?: any) =>
  Boolean(
    (detail?.benefitsExtend || []).some(
      (item: any) =>
        item?.shopBenefitsSendType !== undefined ||
        item?.shopBenefitsSendCycle !== undefined ||
        item?.shopBenefitsSendTypeForAdult !== undefined ||
        item?.shopBenefitsSendValueForAdult !== undefined ||
        item?.shopBenefitsSendTypeForOld !== undefined ||
        item?.shopBenefitsSendValueForOld !== undefined,
    ),
  );
const isShopGrowthBenefit = (detail?: any, isShopPage = false) =>
  Boolean(
    (detail?.benefitsRank === 'SHOP' || isShopPage) &&
    (detail?.basicBenefitsType === 'LEVEL_UP' ||
      detail?.basicBenefitsType === 'GROW_UP' ||
      detail?.benefitsName === '成长礼' ||
      hasShopGrowthExtendFields(detail)),
  );

const BenefitsManagement: React.FC<BenefitsManagementProps> = ({
  pageTitle = '会员权益',
  modalTitlePrefix = '会员权益',
  sections = DEFAULT_SECTIONS,
  switchPermissionKey = BENEFITS_KEY.SWITCH,
}) => {
  const { currentShopId } = useSnapshot(state);
  const isShopPage = useMemo(() => sections.some(section => section.benefitsType === 'SHOP'), [sections]);
  const [loading, setLoading] = useState(false);
  const [benefitLists, setBenefitLists] = useState<Record<string, any[]>>({});
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailMode, setDetailMode] = useState<'view' | 'edit'>('view');
  const [selectedUpgradeLevel, setSelectedUpgradeLevel] = useState<number>(2);
  const [selectedBirthLevel, setSelectedBirthLevel] = useState<number>(1);
  const [selectedOtherLevel, setSelectedOtherLevel] = useState<number>(1);
  const [otherExtendType, setOtherExtendType] = useState<number>(0);
  const [editor, setEditor] = useState<any>(null);
  const [editorHtml, setEditorHtml] = useState<string>('');
  const [form] = Form.useForm();
  const validityType = Form.useWatch('validityType', form);
  const shopBenefitsSendMode = Form.useWatch('shopBenefitsSendType', form);
  const { RangePicker } = DatePicker;
  const baseLevelList = useMemo(() => ['1', '2', '3', '4', '5'], []);
  const baseLevelNums = useMemo(() => baseLevelList.map(Number), [baseLevelList]);
  const levelUpList = useMemo(() => baseLevelList.filter(lv => Number(lv) >= 2), [baseLevelList]);
  const levelUpNums = useMemo(() => levelUpList.map(Number), [levelUpList]);
  const maxLevel = useMemo(() => (baseLevelNums.length ? Math.max(...baseLevelNums) : 5), [baseLevelNums]);
  const normalizeLevelArray = (levels?: string | string[]) => {
    const rawLevels = levels ? (Array.isArray(levels) ? levels : String(levels).split(',')) : baseLevelList;
    const filtered = rawLevels.map(String).filter(lv => baseLevelList.includes(lv));
    return filtered.length ? filtered : baseLevelList;
  };
  const otherLevelOptions = useMemo(() => {
    const labelMap: Record<string, string> = {
      '1': '一级会员',
      '2': '二级会员',
      '3': '三级会员',
      '4': '四级会员',
      '5': '五级会员',
    };
    return baseLevelList.map(lv => ({ label: `${labelMap[lv]}及以上`, value: Number(lv) }));
  }, [baseLevelList]);
  const normalizedOtherLevel = Math.min(selectedOtherLevel || 1, maxLevel);
  const shopBenefitsSendCycleOptions = useMemo(
    () => Array.from({ length: 31 }, (_, index) => ({ label: `${index + 1}`, value: index + 1 })),
    [],
  );
  const shopBenefitSendTypeOptions = useMemo(() => [{ label: '积分', value: 'point' }], []);

  const levelLabel = useMemo(() => '一级会员及以上', []);
  const toolbarConfig = useMemo(() => defaultToolbarConfig, []);
  const canViewBenefit = useMemo(() => checkPermissions(BENEFITS_KEY.VIEW), []);
  const canSaveBenefit = useMemo(() => checkPermissions(BENEFITS_KEY.SAVE), []);
  const canSwitchBenefit = useMemo(() => checkPermissions(switchPermissionKey), [switchPermissionKey]);
  const canDeleteBenefit = useMemo(() => checkPermissions(BENEFITS_KEY.DELETE), []);
  const previewMap: Record<string, string> = {
    LEVEL_UP: 'https://img.alicdn.com/imgextra/i2/155168396/O1CN01b31V7l2BtQTUCCsLk_!!155168396.png',
    FIRST_ORDER: 'https://img.alicdn.com/imgextra/i1/155168396/O1CN01KVRu8m2BtQTTM6GUK_!!155168396.png',
    BIND: 'https://img.alicdn.com/imgextra/i2/155168396/O1CN01gurrml2BtQTSLkmPf_!!155168396.png',
    PET_BIRTH: 'https://img.alicdn.com/imgextra/i1/155168396/O1CN01mDUcQW2BtQTTmYVtS_!!155168396.png',
    FIRST_CREATE_PET: 'https://img.alicdn.com/imgextra/i1/155168396/O1CN01IqlBTP2BtQTY7tCPX_!!155168396.png',
    OTHER: 'https://img.alicdn.com/imgextra/i3/155168396/O1CN01JGt3Jf2BtQTTYEy58_!!155168396.png',
  };
  const previewImg = useMemo(() => {
    if (!detailData) return '';
    if (isCustomBenefitType(detailData.benefitsType)) return previewMap.OTHER;
    return previewMap[detailData.basicBenefitsType] || previewMap.OTHER;
  }, [detailData]);

  const uploadRequiredRule = (messageText: string) => ({
    validator(_: any, value: any) {
      if (!value) return Promise.reject(new Error(messageText));
      if (typeof value === 'object' && Object.keys(value).length === 0) return Promise.reject(new Error(messageText));
      return Promise.resolve();
    },
  });

  const isRichTextEmpty = (html?: string) => {
    if (!html) return true;
    if (/<img\b[^>]*\bsrc\s*=\s*['"][^'"]+['"][^>]*>/i.test(html)) {
      return false;
    }
    const text = html
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/gi, '')
      .trim();
    return !text;
  };

  const overviewRules = (label: string) => [
    { required: true, message: `请输入${label}` },
    { pattern: /^\S{1,50}$/, message: `${label}需在50字内且不可包含空格` },
  ];

  useEffect(() => {
    fetchAll();
  }, [currentShopId]);

  const getValidityRange = (detail: any) =>
    detail?.validityStart && detail?.validityEnd ? [dayjs(detail.validityStart), dayjs(detail.validityEnd)] : undefined;

  const fetchAll = async () => {
    if (!currentShopId) {
      message.warning('请先选择店铺');
      return;
    }
    setLoading(true);
    try {
      const responses = await Promise.all(
        sections.map(section => getBenefitsListApi(currentShopId, { benefitsType: section.benefitsType })),
      );
      const nextBenefitLists: Record<string, any[]> = {};
      responses.forEach((res, index) => {
        const section = sections[index];
        if (res?.code === 200) {
          nextBenefitLists[section.benefitsType] = res.data || [];
        } else {
          message.error(res?.msg || section.fetchErrorText || `获取${section.title}失败`);
        }
      });
      setBenefitLists(nextBenefitLists);
    } catch (error) {
      console.error(error);
      message.error('获取权益失败');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (item: any) => {
    const shopId = state.currentShopId;
    if (!shopId) {
      message.warning('请先选择店铺');
      return;
    }
    const benefitsId = item?.id ?? item?.benefitsId ?? 0;
    setDetailMode('view');
    try {
      const res = await viewBenefitApi(shopId, { benefitsId });
      if (res?.code === 200 && res.data) {
        populateDetail(res.data);
      } else {
        message.error(res?.msg || '获取详情失败');
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const populateDetail = (rawDetail: any) => {
    let detail = rawDetail;
    const normalizedLevels = normalizeLevelArray(detail?.levelArray);
    detail = { ...detail, levelArray: normalizedLevels.join(',') };
    const currentDetailIsShopGrowth = isShopGrowthBenefit(detail, isShopPage);
    // 如果在编辑模式且 benefitsExtend 为空，根据 levelArray 自动生成占位
    if (detailMode === 'edit' && (!Array.isArray(detail.benefitsExtend) || detail.benefitsExtend.length === 0)) {
      const fallbackExtends = currentDetailIsShopGrowth
        ? [
            {
              benefitsApplyLevel: 1,
              shopBenefitsSendType: 'auto',
              shopBenefitsSendCycle: 1,
              shopBenefitsSendTypeForAdult: 'point',
              shopBenefitsSendValueForAdult: undefined,
              shopBenefitsSendTypeForOld: 'point',
              shopBenefitsSendValueForOld: undefined,
              listOverview: undefined,
              detailOverview: undefined,
              buttonText: undefined,
              jdRedirectUrl: undefined,
              tmRedirectUrl: undefined,
            },
          ]
        : normalizedLevels.map((lv: string) => ({
            benefitsApplyLevel: Number(lv),
            benefitsSendType: 'POINT',
            benefitsSendValue: undefined,
            listOverview: undefined,
            detailOverview: undefined,
            buttonText: undefined,
            jdRedirectUrl: undefined,
            tmRedirectUrl: undefined,
          }));
      detail = { ...detail, benefitsExtend: fallbackExtends };
    }

    setDetailData(detail);
    const isLevelUp = detail.basicBenefitsType === 'LEVEL_UP';
    const isBirth = detail.basicBenefitsType === 'PET_BIRTH';
    const isFirstCreatePet = detail.basicBenefitsType === 'FIRST_CREATE_PET';
    const isCustomBenefit = isCustomBenefitType(detail.benefitsType);
    if (currentDetailIsShopGrowth) {
      const primaryExtend = Array.isArray(detail.benefitsExtend) ? detail.benefitsExtend[0] || {} : {};
      form.setFieldsValue({
        benefitsName: detail.benefitsName,
        levelArray: detail.levelArray || baseLevelList.join(','),
        validityType: detail.validityType ?? 0,
        validityStart: detail.validityStart || null,
        validityEnd: detail.validityEnd || null,
        validityRange: getValidityRange(detail),
        listOverview: detail.listOverview || primaryExtend.listOverview,
        detailOverview: detail.detailOverview || primaryExtend.detailOverview,
        cAutoShowStatus: detail.cAutoShowStatus ?? 0,
        iconUrl: detail.iconUrl,
        noIconUrl: detail.noIconUrl,
        shopBenefitsSendType: String(primaryExtend.shopBenefitsSendType || 'auto').toLowerCase(),
        shopBenefitsSendCycle: primaryExtend.shopBenefitsSendCycle ?? 1,
        shopBenefitsSendTypeForAdult: String(primaryExtend.shopBenefitsSendTypeForAdult || 'point').toLowerCase(),
        shopBenefitsSendValueForAdult: primaryExtend.shopBenefitsSendValueForAdult,
        shopBenefitsSendTypeForOld: String(primaryExtend.shopBenefitsSendTypeForOld || 'point').toLowerCase(),
        shopBenefitsSendValueForOld: primaryExtend.shopBenefitsSendValueForOld,
      });
    } else if (isLevelUp || isBirth) {
      if (isLevelUp) {
        setSelectedUpgradeLevel(2);
      }
      if (isBirth) {
        setSelectedBirthLevel(1);
      }
      const perLevelValues: any = {};
      const extend = Array.isArray(detail.benefitsExtend) ? detail.benefitsExtend : [];
      const levelList = isLevelUp ? levelUpNums : baseLevelNums;
      levelList.forEach(lv => {
        const match = extend.find((e: any) => Number(e.benefitsApplyLevel) === lv) || {};
        perLevelValues[`benefitsSendType_${lv}`] = match.benefitsSendType || 'POINT';
        perLevelValues[`benefitsSendValue_${lv}`] = match.benefitsSendValue;
        if (isBirth) {
          perLevelValues[`benefitsSendCouponId_${lv}`] = match.benefitsSendCouponId;
          perLevelValues[`benefitsSendRedirectLink_${lv}`] = match.benefitsSendRedirectLink;
        }
        perLevelValues[`listOverview_${lv}`] = match.listOverview;
        perLevelValues[`detailOverview_${lv}`] = match.detailOverview;
      });
      form.setFieldsValue({
        benefitsName: detail.benefitsName,
        levelArray: isLevelUp ? levelUpList.join(',') : isFirstCreatePet ? baseLevelList.join(',') : detail.levelArray,
        validityType: detail.validityType ?? 0,
        validityStart: detail.validityStart || null,
        validityEnd: detail.validityEnd || null,
        validityRange: getValidityRange(detail),
        listOverview: detail.listOverview || detail.benefitsExtend?.[0]?.listOverview,
        detailOverview: detail.detailOverview || detail.benefitsExtend?.[0]?.detailOverview,
        cAutoShowStatus: detail.basicBenefitsType === 'FIRST_CREATE_PET' ? 1 : (detail.cAutoShowStatus ?? 0),
        iconUrl: detail.iconUrl,
        noIconUrl: detail.noIconUrl,
        ...perLevelValues,
      });
    } else {
      if (isCustomBenefit) {
        const levels = normalizedLevels.map(i => Number(i));
        const startLv = levels.length ? Math.min(...levels) : 1;
        setSelectedOtherLevel(startLv);
        setOtherExtendType(detail.otherBenefitsExtendType ?? 0);
        const perLevelValues: any = {};
        const extend = Array.isArray(detail.benefitsExtend) ? detail.benefitsExtend : [];
        levels.forEach(lv => {
          const match = extend.find((e: any) => Number(e.benefitsApplyLevel) === lv) || {};
          perLevelValues[`listOverview_${lv}`] = match.listOverview;
          perLevelValues[`detailOverview_${lv}`] = match.detailOverview;
          perLevelValues[`buttonText_${lv}`] = match.buttonText;
          perLevelValues[`tmRedirectUrl_${lv}`] = match.tmRedirectUrl;
          perLevelValues[`jdRedirectUrl_${lv}`] = match.jdRedirectUrl;
        });
        form.setFieldsValue({
          benefitsName: detail.benefitsName,
          levelArray: detail.levelArray,
          validityType: 0,
          validityStart: null,
          validityEnd: null,
          validityRange: undefined,
          listOverview: detail.listOverview || detail.benefitsExtend?.[0]?.listOverview,
          detailOverview: detail.detailOverview || detail.benefitsExtend?.[0]?.detailOverview,
          cAutoShowStatus: detail.basicBenefitsType === 'FIRST_CREATE_PET' ? 1 : (detail.cAutoShowStatus ?? 0),
          iconUrl: detail.iconUrl,
          noIconUrl: detail.noIconUrl,
          ...perLevelValues,
        });
      } else {
        form.setFieldsValue({
          benefitsName: detail.benefitsName,
          levelArray: detail.levelArray,
          validityType: detail.validityType ?? 0,
          validityStart: detail.validityStart || null,
          validityEnd: detail.validityEnd || null,
          validityRange: getValidityRange(detail),
          benefitsSendType: detail.benefitsExtend?.[0]?.benefitsSendType || 'POINT',
          benefitsSendValue: detail.benefitsExtend?.[0]?.benefitsSendValue,
          listOverview: detail.listOverview || detail.benefitsExtend?.[0]?.listOverview,
          detailOverview: detail.detailOverview || detail.benefitsExtend?.[0]?.detailOverview,
          cAutoShowStatus: detail.basicBenefitsType === 'FIRST_CREATE_PET' ? 1 : (detail.cAutoShowStatus ?? 0),
          iconUrl: detail.iconUrl,
          noIconUrl: detail.noIconUrl,
        });
      }
    }
    setEditorHtml(detail.validityDescription || '');
    form.setFieldsValue({ validityDescription: detail.validityDescription || '' });
    setDetailVisible(true);
  };

  const handleEdit = async (item: any) => {
    const shopId = state.currentShopId;
    if (!shopId) {
      message.warning('请先选择店铺');
      return;
    }
    const benefitsId = item?.id ?? item?.benefitsId ?? 0;
    setDetailMode('edit');
    try {
      const res = await viewBenefitApi(shopId, { benefitsId });
      if (res?.code === 200 && res.data) {
        populateDetail(res.data);
      } else {
        message.error(res?.msg || '获取详情失败');
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  const handleAddCustomBenefit = (benefitsType: string) => {
    setDetailMode('edit');
    setSelectedOtherLevel(1);
    setOtherExtendType(0);
    setSelectedUpgradeLevel(2);
    setSelectedBirthLevel(1);
    setEditorHtml('');
    form.resetFields();
    const levelArray = baseLevelList.join(',');
    const placeholderExtends = levelArray.split(',').map(lv => ({
      benefitsApplyLevel: Number(lv),
      benefitsSendType: 'POINT',
      benefitsSendValue: 0,
      listOverview: undefined,
      detailOverview: undefined,
      buttonText: undefined,
      jdRedirectUrl: undefined,
      tmRedirectUrl: undefined,
    }));
    const detail = {
      benefitsType,
      basicBenefitsType: benefitsType,
      benefitsName: '',
      levelArray,
      validityType: 0,
      benefitsExtend: placeholderExtends,
      iconUrl: {},
      noIconUrl: {},
      listOverview: '',
      detailOverview: '',
      cAutoShowStatus: 0,
      openStatus: 0,
      showStatus: 0,
      otherBenefitsExtendType: 0,
    };
    populateDetail(detail);
  };

  const buildPayload = (values: any) => {
    if (!detailData) return null;
    const iconUrl = values.iconUrl || detailData.iconUrl || {};
    const noIconUrl = values.noIconUrl || detailData.noIconUrl || {};
    const validityStart = values.validityStart;
    const validityEnd = values.validityEnd;

    const isLevelUp = detailData.basicBenefitsType === 'LEVEL_UP';
    const isBirth = detailData.basicBenefitsType === 'PET_BIRTH';
    const isFirstCreatePet = detailData.basicBenefitsType === 'FIRST_CREATE_PET';
    const isCustomBenefit = isCustomBenefitType(detailData.benefitsType);
    const currentDetailIsShopGrowth = isShopGrowthBenefit(detailData, isShopPage);
    let levelArr: string[] = normalizeLevelArray(values.levelArray);
    let extendList: any[] = [];
    const formOnlyPrefixes = [
      'benefitsSendType_',
      'benefitsSendValue_',
      'benefitsSendCouponId_',
      'benefitsSendRedirectLink_',
      'listOverview_',
      'detailOverview_',
      'buttonText_',
      'tmRedirectUrl_',
      'jdRedirectUrl_',
    ];

    if (currentDetailIsShopGrowth) {
      levelArr = baseLevelList;
      const sendMode = String(values.shopBenefitsSendType || 'auto').toLowerCase();
      extendList = levelArr.map(lv => {
        const base =
          (detailData?.benefitsExtend || []).find((e: any) => Number(e.benefitsApplyLevel) === Number(lv)) ||
          detailData?.benefitsExtend?.[0] ||
          {};
        return {
          benefitsApplyLevel: Number(lv),
          shopBenefitsSendType: sendMode,
          ...(sendMode === 'auto'
            ? {
                shopBenefitsSendCycle: values.shopBenefitsSendCycle,
                shopBenefitsSendTypeForAdult: String(values.shopBenefitsSendTypeForAdult || 'point').toUpperCase(),
                shopBenefitsSendValueForAdult: values.shopBenefitsSendValueForAdult,
                shopBenefitsSendTypeForOld: String(values.shopBenefitsSendTypeForOld || 'point').toUpperCase(),
                shopBenefitsSendValueForOld: values.shopBenefitsSendValueForOld,
              }
            : {}),
          listOverview: values.listOverview,
          detailOverview: values.detailOverview,
          buttonText: base.buttonText,
          jdRedirectUrl: base.jdRedirectUrl,
          tmRedirectUrl: base.tmRedirectUrl,
        };
      });
    } else if (isLevelUp) {
      levelArr = levelUpList;
      extendList = levelArr.map(lv => {
        const base =
          (detailData?.benefitsExtend || []).find((e: any) => Number(e.benefitsApplyLevel) === Number(lv)) ||
          detailData?.benefitsExtend?.[0] ||
          {};
        return {
          benefitsApplyLevel: Number(lv),
          benefitsSendType: values[`benefitsSendType_${lv}`] || 'POINT',
          benefitsSendValue: values[`benefitsSendValue_${lv}`],
          listOverview: values[`listOverview_${lv}`],
          detailOverview: values[`detailOverview_${lv}`],
          buttonText: base.buttonText,
          jdRedirectUrl: base.jdRedirectUrl,
          tmRedirectUrl: base.tmRedirectUrl,
        };
      });
    } else if (isBirth) {
      levelArr = baseLevelList;
      extendList = levelArr.map(lv => {
        const base =
          (detailData?.benefitsExtend || []).find((e: any) => Number(e.benefitsApplyLevel) === Number(lv)) ||
          detailData?.benefitsExtend?.[0] ||
          {};
        const sendType = values[`benefitsSendType_${lv}`] || 'POINT';
        const isThirdPlat = sendType === 'THIRD_PLAT';
        return {
          benefitsApplyLevel: Number(lv),
          benefitsSendType: sendType,
          benefitsSendValue: values[`benefitsSendValue_${lv}`],
          benefitsSendCouponId: isThirdPlat ? values[`benefitsSendCouponId_${lv}`] : undefined,
          benefitsSendRedirectLink: isThirdPlat ? values[`benefitsSendRedirectLink_${lv}`] : undefined,
          listOverview: values[`listOverview_${lv}`],
          detailOverview: values[`detailOverview_${lv}`],
          buttonText: base.buttonText,
          jdRedirectUrl: base.jdRedirectUrl,
          tmRedirectUrl: base.tmRedirectUrl,
        };
      });
    } else if (isCustomBenefit) {
      const startLv = Math.min(selectedOtherLevel || 1, maxLevel);
      levelArr = baseLevelList.filter(lv => Number(lv) >= startLv);
      if (otherExtendType === 0) {
        const baseLv = levelArr[0];
        const base =
          (detailData?.benefitsExtend || []).find((e: any) => Number(e.benefitsApplyLevel) === Number(baseLv)) || {};
        const baseVals = {
          listOverview: values[`listOverview_${baseLv}`],
          detailOverview: values[`detailOverview_${baseLv}`],
          buttonText: values[`buttonText_${baseLv}`],
          tmRedirectUrl: values[`tmRedirectUrl_${baseLv}`],
          jdRedirectUrl: values[`jdRedirectUrl_${baseLv}`],
          benefitsSendType: 'POINT',
          benefitsSendValue: 0,
          buttonTextDefault: base.buttonText,
          jdRedirectUrlDefault: base.jdRedirectUrl,
          tmRedirectUrlDefault: base.tmRedirectUrl,
        };
        extendList = levelArr.map(lv => ({
          benefitsApplyLevel: Number(lv),
          benefitsSendType: 'POINT',
          benefitsSendValue: 0,
          listOverview: baseVals.listOverview,
          detailOverview: baseVals.detailOverview,
          buttonText: baseVals.buttonText,
          jdRedirectUrl: baseVals.jdRedirectUrl,
          tmRedirectUrl: baseVals.tmRedirectUrl,
        }));
      } else {
        extendList = levelArr.map(lv => {
          const base =
            (detailData?.benefitsExtend || []).find((e: any) => Number(e.benefitsApplyLevel) === Number(lv)) || {};
          return {
            benefitsApplyLevel: Number(lv),
            benefitsSendType: 'POINT',
            benefitsSendValue: 0,
            listOverview: values[`listOverview_${lv}`],
            detailOverview: values[`detailOverview_${lv}`],
            buttonText: values[`buttonText_${lv}`],
            jdRedirectUrl: values[`jdRedirectUrl_${lv}`],
            tmRedirectUrl: values[`tmRedirectUrl_${lv}`],
            buttonTextDefault: base.buttonText,
            jdRedirectUrlDefault: base.jdRedirectUrl,
            tmRedirectUrlDefault: base.tmRedirectUrl,
          };
        });
      }
    } else {
      if (isFirstCreatePet) {
        levelArr = baseLevelList;
      }
      const commonExtend = {
        benefitsSendType: values.benefitsSendType,
        benefitsSendValue: values.benefitsSendValue,
        listOverview: values.listOverview,
        detailOverview: values.detailOverview,
        buttonText: detailData.benefitsExtend?.[0]?.buttonText,
        jdRedirectUrl: detailData.benefitsExtend?.[0]?.jdRedirectUrl,
        tmRedirectUrl: detailData.benefitsExtend?.[0]?.tmRedirectUrl,
      };
      extendList = levelArr.map((lv: string) => ({
        ...commonExtend,
        benefitsApplyLevel: Number(lv),
      }));
    }

    const formatDatetime = (val: any) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : null);
    const payloadValues = Object.fromEntries(
      Object.entries(values).filter(
        ([key]) =>
          ![
            'validityRange',
            'shopBenefitsSendType',
            'shopBenefitsSendCycle',
            'shopBenefitsSendTypeForAdult',
            'shopBenefitsSendValueForAdult',
            'shopBenefitsSendTypeForOld',
            'shopBenefitsSendValueForOld',
          ].includes(key) && !formOnlyPrefixes.some(prefix => key.startsWith(prefix)),
      ),
    );
    return {
      ...detailData,
      ...payloadValues,
      openStatus: 0,
      levelArray: levelArr.join(','),
      otherBenefitsExtendType: isCustomBenefit ? otherExtendType : 1,
      validityDescription: values.validityDescription || editorHtml,
      validityStart: validityType === 1 ? formatDatetime(validityStart) : null,
      validityEnd: validityType === 1 ? formatDatetime(validityEnd) : null,
      iconUrl,
      noIconUrl,
      benefitsExtend: extendList,
      listOverview: values.listOverview,
      detailOverview: values.detailOverview,
      cAutoShowStatus: detailData.basicBenefitsType === 'FIRST_CREATE_PET' ? 1 : values.cAutoShowStatus,
    };
  };

  useEffect(() => {
    return () => {
      if (editor) {
        editor.destroy?.();
      }
    };
  }, [editor]);

  const toggleSwitch = async (item: any, switchType: 'OPEN' | 'SHOW', switchValue: 0 | 1, shouldFetch = true) => {
    const shopId = state.currentShopId;
    if (!shopId) {
      message.warning('请先选择店铺');
      return;
    }
    const benefitsId = item?.id ?? item?.benefitsId;
    try {
      const res = await switchBenefitApi(shopId, { switchValue, switchType, benefitsId });
      if (res?.code === 200) {
        message.success('操作成功');
        if (shouldFetch) fetchAll();
      } else {
        message.error(res?.msg || '操作失败');
      }
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleToggleOpen = async (item: any, checked: boolean) => {
    if (checked) {
      handleEdit(item);
    } else {
      // 启用 -> 未启用 传 1（1表示未启用）
      await toggleSwitch(item, 'OPEN', 1, false);
      await toggleSwitch(item, 'SHOW', 1, true);
    }
  };

  const handleToggleShow = (item: any, checked: boolean) => {
    if (item.openStatus !== 0) return;
    // 0 表示显示，1 表示不显示
    toggleSwitch(item, 'SHOW', checked ? 0 : 1);
  };

  const handleDeleteBenefit = (item: any) => {
    const shopId = state.currentShopId;
    if (!shopId) {
      message.warning('请先选择店铺');
      return;
    }
    Modal.confirm({
      title: '确认删除该权益？',
      okText: '删除',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await deleteBenefitApi(shopId, item?.id ?? item?.benefitsId);
          if (res?.code === 200) {
            message.success('删除成功');
            fetchAll();
          } else {
            message.error(res?.msg || '删除失败');
          }
        } catch (error) {
          message.error('删除失败');
        }
      },
    });
  };

  const resetDetailState = () => {
    form.resetFields();
    setDetailData(null);
    setEditorHtml('');
    setSelectedUpgradeLevel(2);
    setSelectedBirthLevel(1);
    setSelectedOtherLevel(1);
    setOtherExtendType(0);
  };

  const currentIsCustomBenefit = isCustomBenefitType(detailData?.benefitsType);
  const currentIsShopGrowthBenefit = isShopGrowthBenefit(detailData, isShopPage);

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <Title
          level={3}
          className={styles.title}>
          {pageTitle}
        </Title>
        <Button onClick={fetchAll}>刷新</Button>
      </div>
      <Spin spinning={loading}>
        {sections.map(section => (
          <BenefitSection
            key={section.benefitsType}
            title={section.title}
            data={benefitLists[section.benefitsType] || []}
            showDelete={section.showDelete}
            allowView={section.allowView ?? canViewBenefit}
            allowEdit={section.allowEdit ?? canSaveBenefit}
            allowSwitch={section.allowSwitch ?? canSwitchBenefit}
            allowDelete={section.allowDelete ?? canDeleteBenefit}
            onView={handleView}
            onEdit={handleEdit}
            onToggleOpen={handleToggleOpen}
            onToggleShow={handleToggleShow}
            onDelete={handleDeleteBenefit}
            extraNode={
              section.showAddCard ? (
                <div
                  className={styles.addCard}
                  onClick={() => handleAddCustomBenefit(section.benefitsType)}>
                  <PlusOutlined className={styles.addIcon} />
                  {section.addButtonText || `添加${section.title}`}
                </div>
              ) : undefined
            }
          />
        ))}
      </Spin>

      <Modal
        title={`${modalTitlePrefix}-${detailData?.benefitsName || ''}设置`}
        open={detailVisible}
        width={1060}
        destroyOnHidden
        afterOpenChange={open => {
          if (!open) {
            setEditor(null);
          }
        }}
        onCancel={() => {
          setDetailVisible(false);
          resetDetailState();
        }}
        footer={detailMode === 'view' ? null : undefined}
        okButtonProps={detailMode === 'edit' ? { disabled: !canSaveBenefit } : undefined}
        onOk={
          detailMode === 'edit'
            ? async () => {
                if (!canSaveBenefit) {
                  message.error('暂无保存权限');
                  return;
                }
                const shopId = state.currentShopId;
                if (!shopId || !detailData) {
                  setDetailVisible(false);
                  resetDetailState();
                  return;
                }
                try {
                  if (isRichTextEmpty(form.getFieldValue('validityDescription') || editorHtml)) {
                    message.error('请输入权益说明');
                    return;
                  }
                  const values = await form.validateFields();
                  const payload = buildPayload(values);
                  if (!payload) return;
                  const res = await saveOrUpdateBenefitApi(shopId, payload);
                  if (res?.code === 200) {
                    message.success('保存成功');
                    setDetailVisible(false);
                    resetDetailState();
                    fetchAll();
                  } else {
                    message.error(res?.msg || '保存失败');
                  }
                } catch (error) {
                  if ((error as any)?.errorFields) return;
                  message.error('保存失败');
                }
              }
            : undefined
        }>
        <div className={styles.modalBody}>
          <div className={styles.formWrap}>
            <Form
              layout="horizontal"
              labelCol={{ style: { width: 200 } }}
              wrapperCol={{ style: { flex: 1 } }}
              form={form}
              onValuesChange={(_, allValues) => {
                const payload = buildPayload(allValues);
                if (payload) console.log('权益表单变更：', payload);
              }}>
              <Form.Item
                name="validityDescription"
                hidden
                rules={[
                  {
                    validator: (_, value) =>
                      isRichTextEmpty(value) ? Promise.reject(new Error('请输入权益说明')) : Promise.resolve(),
                  },
                ]}>
                <Input />
              </Form.Item>
              <Form.Item
                name="validityStart"
                hidden>
                <Input />
              </Form.Item>
              <Form.Item
                name="validityEnd"
                hidden>
                <Input />
              </Form.Item>
              {currentIsShopGrowthBenefit ? (
                <Form.Item label="权益名称">
                  <span>{detailData?.benefitsName || '--'}</span>
                </Form.Item>
              ) : (
                <Form.Item
                  label="权益名称"
                  name="benefitsName"
                  rules={[
                    { required: true, message: '请输入权益名称' },
                    { pattern: /^\S{1,6}$/, message: '最多6个字符，且不可包含空格' },
                  ]}>
                  <Input
                    disabled={!currentIsCustomBenefit}
                    placeholder="请输入"
                    maxLength={6}
                    showCount
                  />
                </Form.Item>
              )}
              {currentIsShopGrowthBenefit ? (
                <Form.Item label="权益解锁等级">
                  <span>一级会员及以上</span>
                </Form.Item>
              ) : detailData?.basicBenefitsType === 'LEVEL_UP' ? (
                <Form.Item label="权益解锁等级">
                  <span>二级会员及以上</span>
                </Form.Item>
              ) : (
                <>
                  {detailData?.basicBenefitsType === 'PET_BIRTH' ? (
                    <Form.Item label="权益解锁等级">
                      <span>一级会员及以上</span>
                    </Form.Item>
                  ) : currentIsCustomBenefit ? (
                    <Form.Item label="权益解锁等级">
                      <Radio.Group
                        value={normalizedOtherLevel}
                        onChange={e => {
                          const val = e.target.value;
                          setSelectedOtherLevel(val);
                          const levelArr = baseLevelList.filter(lv => Number(lv) >= val);
                          form.setFieldsValue({ levelArray: levelArr.join(',') });
                        }}
                        options={otherLevelOptions}
                      />
                    </Form.Item>
                  ) : (
                    <Form.Item label="权益解锁等级">
                      <span>{levelLabel}</span>
                    </Form.Item>
                  )}
                </>
              )}
              <Form.Item
                label="生效周期"
                required>
                {currentIsCustomBenefit ? (
                  <Form.Item
                    name="validityType"
                    noStyle
                    initialValue={0}>
                    <Radio.Group>
                      <Radio value={0}>长期有效</Radio>
                    </Radio.Group>
                  </Form.Item>
                ) : (
                  <Space>
                    <Form.Item
                      name="validityType"
                      noStyle
                      initialValue={0}>
                      <Radio.Group>
                        <Radio value={0}>长期有效</Radio>
                        <Radio value={1}>指定周期</Radio>
                      </Radio.Group>
                    </Form.Item>
                    {validityType === 1 && (
                      <Form.Item
                        name="validityRange"
                        noStyle
                        rules={[{ required: true, message: '请选择生效时间' }]}>
                        <RangePicker
                          style={{ width: 360 }}
                          showTime
                          format="YYYY-MM-DD HH:mm:ss"
                          onChange={(_, dateStrings) => {
                            form.setFieldsValue({
                              validityStart: dateStrings?.[0] || null,
                              validityEnd: dateStrings?.[1] || null,
                            });
                          }}
                        />
                      </Form.Item>
                    )}
                  </Space>
                )}
              </Form.Item>
              {!currentIsShopGrowthBenefit &&
                detailData?.basicBenefitsType !== 'LEVEL_UP' &&
                !currentIsCustomBenefit &&
                detailData?.basicBenefitsType !== 'PET_BIRTH' && (
                  <>
                    <Form.Item
                      label="发放权益类型"
                      name="benefitsSendType"
                      initialValue="POINT"
                      getValueProps={() => ({ value: '积分' })}>
                      <Input disabled />
                    </Form.Item>
                    <Form.Item
                      label="发放数量"
                      name="benefitsSendValue"
                      rules={[{ required: true, message: '请输入发放数量' }]}>
                      <InputNumber
                        style={{ width: '100%' }}
                        min={1}
                        max={999999999}
                        precision={0}
                        placeholder="请输入"
                        controls={false}
                        stringMode
                      />
                    </Form.Item>
                  </>
                )}
              <div className={styles.iconRow}>
                <Form.Item
                  label="解锁图标"
                  name="iconUrl"
                  className={styles.iconItem}
                  rules={currentIsCustomBenefit ? [] : [uploadRequiredRule('请上传解锁图标')]}>
                  <ImageUploader
                    sync={1}
                    maxSizeKB={2048}
                    tip="建议尺寸：80x80px，格式：JPG/PNG，大小：不超过2MB"
                  />
                </Form.Item>
                <Form.Item
                  label="未解锁图标"
                  name="noIconUrl"
                  className={styles.iconItem}
                  rules={currentIsCustomBenefit ? [] : [uploadRequiredRule('请上传未解锁图标')]}>
                  <ImageUploader
                    sync={1}
                    maxSizeKB={2048}
                    tip="建议尺寸：80x80px，格式：JPG/PNG，大小：不超过2MB"
                  />
                </Form.Item>
              </div>
              {currentIsShopGrowthBenefit && (
                <>
                  <div className={styles.sectionTitle}>权益发放</div>
                  <Form.Item
                    label="自动发放"
                    required>
                    <Form.Item
                      name="shopBenefitsSendType"
                      noStyle
                      initialValue="auto"
                      rules={[{ required: true, message: '请选择自动发放方式' }]}>
                      <Radio.Group
                        onChange={e => {
                          if (e.target.value !== 'auto') {
                            form.setFieldsValue({ shopBenefitsSendCycle: undefined });
                          }
                        }}>
                        <Radio value="auto">自动发放</Radio>
                        <Radio value="manual">不自动发放</Radio>
                      </Radio.Group>
                    </Form.Item>
                  </Form.Item>
                  {shopBenefitsSendMode === 'auto' && (
                    <>
                      <Form.Item
                        label="发放周期"
                        required>
                        <Space>
                          <span>每月</span>
                          <Form.Item
                            name="shopBenefitsSendCycle"
                            noStyle
                            rules={[{ required: true, message: '请选择发放周期' }]}>
                            <Select
                              style={{ width: 92 }}
                              options={shopBenefitsSendCycleOptions}
                              placeholder="请选择"
                            />
                          </Form.Item>
                          <span>日</span>
                          <span className={styles.inlineHint}>所选日期大于当月总天数，在当月最后一天发放</span>
                        </Space>
                      </Form.Item>

                      <div className={styles.levelBlock}>
                        <div className={styles.levelTitle}>成年礼</div>
                        <Form.Item
                          label="发放权益类型"
                          name="shopBenefitsSendTypeForAdult"
                          initialValue="point"
                          rules={[{ required: true, message: '请选择成年礼发放权益类型' }]}>
                          <Select
                            style={{ width: 240 }}
                            options={shopBenefitSendTypeOptions}
                            placeholder="请选择"
                          />
                        </Form.Item>
                        <Form.Item
                          label="发放数量"
                          required>
                          <Space>
                            <Form.Item
                              name="shopBenefitsSendValueForAdult"
                              noStyle
                              rules={[{ required: true, message: '请输入成年礼发放数量' }]}>
                              <InputNumber
                                style={{ width: 180 }}
                                min={0}
                                max={99999999}
                                precision={0}
                                placeholder="请输入"
                                controls={false}
                              />
                            </Form.Item>
                            <span>积分</span>
                          </Space>
                        </Form.Item>
                      </div>

                      <div className={styles.levelBlock}>
                        <div className={styles.levelTitle}>老友礼</div>
                        <Form.Item
                          label="发放权益类型"
                          name="shopBenefitsSendTypeForOld"
                          initialValue="point"
                          rules={[{ required: true, message: '请选择老友礼发放权益类型' }]}>
                          <Select
                            style={{ width: 240 }}
                            options={shopBenefitSendTypeOptions}
                            placeholder="请选择"
                          />
                        </Form.Item>
                        <Form.Item
                          label="发放数量"
                          required>
                          <Space>
                            <Form.Item
                              name="shopBenefitsSendValueForOld"
                              noStyle
                              rules={[{ required: true, message: '请输入老友礼发放数量' }]}>
                              <InputNumber
                                style={{ width: 180 }}
                                min={0}
                                max={99999999}
                                precision={0}
                                placeholder="请输入"
                                controls={false}
                              />
                            </Form.Item>
                            <span>积分</span>
                          </Space>
                        </Form.Item>
                      </div>
                    </>
                  )}
                </>
              )}
              {!currentIsShopGrowthBenefit &&
                (detailData?.basicBenefitsType === 'LEVEL_UP' || detailData?.basicBenefitsType === 'PET_BIRTH') &&
                (detailData?.basicBenefitsType === 'LEVEL_UP' ? levelUpList : baseLevelList).map(lv => (
                  <div
                    key={lv}
                    className={styles.levelBlock}>
                    <div className={styles.levelTitle}>{`${lv}级会员`}</div>
                    {detailData?.basicBenefitsType === 'PET_BIRTH' ? (
                      <Form.Item
                        label="发放权益类型"
                        name={`benefitsSendType_${lv}`}
                        initialValue="POINT"
                        rules={[{ required: true, message: '请选择发放权益类型' }]}>
                        <Select
                          options={[
                            { label: '积分', value: 'POINT' },
                            { label: '三方平台权益', value: 'THIRD_PLAT' },
                          ]}
                          placeholder="请选择"
                          onChange={() => {
                            form.setFieldsValue({
                              [`benefitsSendValue_${lv}`]: undefined,
                            });
                          }}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        label="发放权益类型"
                        name={`benefitsSendType_${lv}`}
                        initialValue="POINT"
                        getValueProps={() => ({ value: '积分' })}>
                        <Input disabled />
                      </Form.Item>
                    )}
                    <Form.Item
                      shouldUpdate
                      noStyle>
                      {({ getFieldValue }) => {
                        const sendType = getFieldValue(`benefitsSendType_${lv}`) || 'POINT';
                        if (detailData?.basicBenefitsType === 'PET_BIRTH' && sendType === 'THIRD_PLAT') {
                          return (
                            <>
                              <Form.Item
                                label="权益明细名称"
                                name={`benefitsSendValue_${lv}`}
                                rules={[{ required: true, message: '请输入权益明细名称' }]}>
                                <Input placeholder="请输入" />
                              </Form.Item>
                              <Form.Item
                                label="三方平台优惠券ID"
                                name={`benefitsSendCouponId_${lv}`}
                                rules={[{ required: true, message: '请输入三方平台优惠券ID' }]}>
                                <Input placeholder="请输入" />
                              </Form.Item>
                              <Form.Item
                                label="三方平台商品兑换链接"
                                name={`benefitsSendRedirectLink_${lv}`}
                                rules={[{ required: true, message: '请输入三方平台商品兑换链接' }]}>
                                <Input placeholder="请输入" />
                              </Form.Item>
                            </>
                          );
                        }
                        return (
                          <Form.Item
                            label="发放数量"
                            name={`benefitsSendValue_${lv}`}
                            rules={[{ required: true, message: '请输入发放数量' }]}>
                            <InputNumber
                              style={{ width: '100%' }}
                              min={1}
                              max={99999999}
                              precision={0}
                              placeholder="请输入"
                              controls={false}
                              stringMode
                            />
                          </Form.Item>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      label="列表简介"
                      name={`listOverview_${lv}`}
                      rules={overviewRules('列表简介')}>
                      <Input
                        placeholder="请输入"
                        showCount
                        maxLength={50}
                      />
                    </Form.Item>
                    <Form.Item
                      label="详情页权益简介"
                      name={`detailOverview_${lv}`}
                      rules={overviewRules('详情页权益简介')}>
                      <Input
                        placeholder="请输入"
                        showCount
                        maxLength={50}
                      />
                    </Form.Item>
                  </div>
                ))}
              {currentIsCustomBenefit && (
                <>
                  <Form.Item label="权益说明">
                    <Radio.Group
                      value={otherExtendType}
                      onChange={e => setOtherExtendType(e.target.value)}
                      options={[
                        { label: '统一设置', value: 0 },
                        { label: '按等级分别设置', value: 1 },
                      ]}
                    />
                  </Form.Item>
                  {(otherExtendType === 0
                    ? [String(normalizedOtherLevel)]
                    : baseLevelList.filter(lv => Number(lv) >= normalizedOtherLevel)
                  ).map(lv => (
                    <div
                      key={lv}
                      className={styles.levelBlock}>
                      {otherExtendType === 1 ? <div className={styles.levelTitle}>{`${lv}级会员`}</div> : null}
                      <Form.Item
                        label="列表页权益简介"
                        name={`listOverview_${lv}`}
                        rules={[]}>
                        <Input
                          placeholder="请输入"
                          showCount
                          maxLength={50}
                        />
                      </Form.Item>
                      <Form.Item
                        label="详情页权益简介"
                        name={`detailOverview_${lv}`}
                        rules={overviewRules('详情页权益简介')}>
                        <Input
                          placeholder="请输入"
                          showCount
                          maxLength={50}
                        />
                      </Form.Item>
                      <Form.Item
                        label="按钮文案"
                        name={`buttonText_${lv}`}
                        rules={[
                          {
                            validator: (_, value) => {
                              if (!value) return Promise.resolve();
                              return /^\S{1,10}$/.test(value)
                                ? Promise.resolve()
                                : Promise.reject(new Error('最多10个字符，且不可包含空格'));
                            },
                          },
                        ]}>
                        <Input
                          placeholder="请输入"
                          maxLength={10}
                          showCount
                        />
                      </Form.Item>
                      <Form.Item
                        label="天猫平台跳转链接"
                        name={`tmRedirectUrl_${lv}`}
                        rules={[]}>
                        <Input placeholder="请输入" />
                      </Form.Item>
                      <Form.Item
                        label="京东平台跳转链接"
                        name={`jdRedirectUrl_${lv}`}
                        rules={[]}>
                        <Input placeholder="请输入" />
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
              {(currentIsShopGrowthBenefit ||
                currentIsCustomBenefit ||
                ['PET_BIRTH', 'BIND', 'FIRST_ORDER', 'LEVEL_UP'].includes(detailData?.basicBenefitsType || '')) && (
                <>
                  {currentIsShopGrowthBenefit && <div className={styles.sectionTitle}>权益说明</div>}
                  <Form.Item
                    label={
                      currentIsShopGrowthBenefit ||
                      detailData?.basicBenefitsType === 'BIND' ||
                      detailData?.basicBenefitsType === 'FIRST_ORDER'
                        ? '列表页权益简介'
                        : '未达等级列表页权益简介'
                    }
                    name="listOverview"
                    rules={
                      currentIsCustomBenefit
                        ? []
                        : overviewRules(currentIsShopGrowthBenefit ? '列表页权益简介' : '未达等级列表页权益简介')
                    }>
                    <Input
                      placeholder="请输入"
                      showCount
                      maxLength={50}
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      currentIsShopGrowthBenefit ||
                      detailData?.basicBenefitsType === 'BIND' ||
                      detailData?.basicBenefitsType === 'FIRST_ORDER'
                        ? '详情页权益简介'
                        : '未达等级详情页权益简介'
                    }
                    name="detailOverview"
                    rules={
                      currentIsCustomBenefit
                        ? []
                        : overviewRules(currentIsShopGrowthBenefit ? '详情页权益简介' : '未达等级详情页权益简介')
                    }>
                    <Input
                      placeholder="请输入"
                      showCount
                      maxLength={50}
                    />
                  </Form.Item>
                </>
              )}
              <Form.Item
                label="权益说明"
                required>
                <div className={styles.editorWrap}>
                  {editor && (
                    <Toolbar
                      editor={editor}
                      defaultConfig={toolbarConfig as any}
                      className={styles.toolbar}
                    />
                  )}
                  <Editor
                    defaultConfig={defaultEditorConfig as any}
                    value={editorHtml}
                    onCreated={setEditor}
                    onChange={(ed: any) => {
                      const html = ed.getHtml();
                      setEditorHtml(html);
                      form.setFieldsValue({ validityDescription: html });
                    }}
                    className={styles.editorInner}
                  />
                </div>
              </Form.Item>
              {detailData?.basicBenefitsType !== 'FIRST_CREATE_PET' && (
                <Form.Item
                  label="C端列表展示"
                  name="cAutoShowStatus"
                  initialValue={0}
                  valuePropName="checked"
                  getValueFromEvent={e => (e?.target?.checked ? 0 : 1)}
                  getValueProps={value => ({ checked: value === 0 })}>
                  <Checkbox>C端权益列表自动展示</Checkbox>
                </Form.Item>
              )}
            </Form>
          </div>
          <div className={styles.previewWrap}>
            {previewImg ? (
              <img
                src={previewImg}
                alt="C端示意"
                className={styles.previewImg}
              />
            ) : null}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default BenefitsManagement;
